import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users, Building2, FileText, Package, TrendingUp, 
  Shield, Eye, EyeOff, Search, ChevronLeft, ChevronRight,
  Crown, Zap, Rocket, Calendar, Activity, RefreshCw,
  Trash2, RotateCcw, AlertTriangle, X
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [signupData, setSignupData] = useState([]);
  const [usageData, setUsageData] = useState(null);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Pagination
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [orgPage, setOrgPage] = useState(1);
  const [orgTotal, setOrgTotal] = useState(0);
  
  // Search
  const [userSearch, setUserSearch] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  
  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Delete modals
  const [deleteModal, setDeleteModal] = useState({ show: false, type: null, item: null, permanent: false });
  const [deletePreview, setDeletePreview] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchSignupAnalytics();
      fetchUsageAnalytics();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && activeTab === 'users') {
      fetchUsers();
    }
  }, [isAdmin, activeTab, userPage, userSearch]);

  useEffect(() => {
    if (isAdmin && activeTab === 'organizations') {
      fetchOrganizations();
    }
  }, [isAdmin, activeTab, orgPage, orgSearch]);

  const checkAdminAccess = async () => {
    try {
      const response = await apiClient.get('/admin/check');
      if (response.data.is_platform_admin) {
        setIsAdmin(true);
      } else {
        toast.error('Access denied. Platform admin required.');
        navigate('/');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      toast.error('Access denied');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchSignupAnalytics = async () => {
    try {
      const response = await apiClient.get('/admin/analytics/signups?days=30');
      setSignupData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch signup analytics:', error);
    }
  };

  const fetchUsageAnalytics = async () => {
    try {
      const response = await apiClient.get('/admin/analytics/usage');
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage analytics:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({ page: userPage, limit: 20 });
      if (userSearch) params.append('search', userSearch);
      const response = await apiClient.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setUserTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const params = new URLSearchParams({ page: orgPage, limit: 20 });
      if (orgSearch) params.append('search', orgSearch);
      const response = await apiClient.get(`/admin/organizations?${params}`);
      setOrganizations(response.data.organizations);
      setOrgTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const handlePlanChange = async (orgId, newPlan) => {
    try {
      await apiClient.patch(`/admin/organizations/${orgId}/plan`, { plan: newPlan });
      toast.success(`Plan updated to ${newPlan}`);
      fetchOrganizations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSavingPassword(true);
    try {
      await apiClient.post('/admin/password', {
        current_password: passwordData.current_password || null,
        new_password: passwordData.new_password
      });
      toast.success('Password updated successfully');
      setShowPasswordForm(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  // Delete functions
  const openDeleteModal = async (type, item, permanent = false) => {
    setDeleteModal({ show: true, type, item, permanent });
    setConfirmName('');
    setDeletePreview(null);
    
    // Fetch deletion preview for organizations
    if (type === 'organization' && permanent) {
      try {
        const response = await apiClient.get(`/admin/organizations/${item.id}/deletion-preview`);
        setDeletePreview(response.data);
      } catch (error) {
        console.error('Failed to fetch deletion preview:', error);
      }
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, type: null, item: null, permanent: false });
    setConfirmName('');
    setDeletePreview(null);
  };

  const handleSoftDeleteUser = async (userId) => {
    setDeleting(true);
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      toast.success('User deactivated successfully');
      fetchUsers();
      fetchStats();
      closeDeleteModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await apiClient.post(`/admin/users/${userId}/restore`);
      toast.success('User restored successfully');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    }
  };

  const handlePermanentDeleteUser = async (userId) => {
    setDeleting(true);
    try {
      await apiClient.delete(`/admin/users/${userId}/permanent`);
      toast.success('User permanently deleted');
      fetchUsers();
      fetchStats();
      closeDeleteModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleSoftDeleteOrg = async (orgId) => {
    setDeleting(true);
    try {
      const response = await apiClient.delete(`/admin/organizations/${orgId}`);
      toast.success(response.data.message);
      fetchOrganizations();
      fetchStats();
      closeDeleteModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate organization');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreOrg = async (orgId) => {
    try {
      const response = await apiClient.post(`/admin/organizations/${orgId}/restore`);
      toast.success(response.data.message);
      fetchOrganizations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore organization');
    }
  };

  const handlePermanentDeleteOrg = async (orgId, orgName) => {
    if (confirmName.trim().toLowerCase() !== orgName.trim().toLowerCase()) {
      toast.error(`Please type "${orgName}" to confirm deletion`);
      return;
    }
    
    setDeleting(true);
    try {
      const response = await apiClient.delete(`/admin/organizations/${orgId}/permanent?confirm_name=${encodeURIComponent(confirmName)}`);
      toast.success(response.data.message);
      fetchOrganizations();
      fetchStats();
      closeDeleteModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  const getPlanBadge = (plan) => {
    const badges = {
      free: { bg: 'bg-slate-100', text: 'text-slate-700', icon: null },
      pro: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <Zap size={12} /> },
      enterprise: { bg: 'bg-purple-100', text: 'text-purple-700', icon: <Crown size={12} /> }
    };
    const badge = badges[plan] || badges.free;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {(plan || 'free').toUpperCase()}
      </span>
    );
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, maxHeight = 120 }) => {
    if (!data || data.length === 0) return null;
    const maxValue = Math.max(...data.map(d => d.signups), 1);
    const recentData = data.slice(-14); // Last 14 days
    
    return (
      <div className="flex items-end gap-1 h-32">
        {recentData.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center">
            <div 
              className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
              style={{ height: `${(d.signups / maxValue) * maxHeight}px`, minHeight: d.signups > 0 ? '4px' : '0' }}
              title={`${d.date}: ${d.signups} signups`}
            />
            <span className="text-[8px] text-slate-400 mt-1 rotate-45 origin-left">
              {d.date.slice(5)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-slate-900 text-white py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="text-amber-400" size={28} />
            <div>
              <h1 className="text-2xl font-bold">Platform Admin Dashboard</h1>
              <p className="text-slate-400 text-sm">Cloud RFQ Management Console</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={() => setShowPasswordForm(!showPasswordForm)}
            >
              {showPasswordForm ? 'Hide' : 'Change Password'}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-slate-300 hover:bg-slate-800"
              onClick={() => navigate('/')}
            >
              Back to App
            </Button>
          </div>
        </div>
      </div>

      {/* Password Change Form */}
      {showPasswordForm && (
        <div className="bg-amber-50 border-b border-amber-200 py-4 px-6">
          <div className="max-w-7xl mx-auto">
            <form onSubmit={handlePasswordChange} className="flex items-end gap-4 flex-wrap">
              <div>
                <Label className="text-xs text-amber-800">Current Password (if set)</Label>
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  placeholder="Leave empty if not set"
                  className="w-48"
                />
              </div>
              <div>
                <Label className="text-xs text-amber-800">New Password *</Label>
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  required
                  className="w-48"
                />
              </div>
              <div>
                <Label className="text-xs text-amber-800">Confirm Password *</Label>
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  required
                  className="w-48"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowPasswords(!showPasswords)}
              >
                {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
              <Button type="submit" disabled={savingPassword} size="sm">
                {savingPassword ? 'Saving...' : 'Update Password'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-6">
            {['overview', 'users', 'organizations'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <StatCard icon={<Users />} label="Total Users" value={stats.totals.users} color="blue" />
              <StatCard icon={<Building2 />} label="Organizations" value={stats.totals.organizations} color="purple" />
              <StatCard icon={<FileText />} label="RFQs Created" value={stats.totals.rfqs} color="green" />
              <StatCard icon={<Package />} label="Purchase Reqs" value={stats.totals.purchase_requisitions} color="amber" />
              <StatCard icon={<Users />} label="Suppliers" value={stats.totals.suppliers} color="indigo" />
              <StatCard icon={<Activity />} label="Quotes" value={stats.totals.quotes} color="pink" />
            </div>

            {/* User Stats & Signup Trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Stats */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Users size={20} className="text-blue-600" />
                  User Statistics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Active (Last 30 days)</span>
                    <span className="font-semibold text-green-600">{stats.users.active_last_30_days}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Google Auth Users</span>
                    <span className="font-semibold">{stats.users.google_auth}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">Email Auth Users</span>
                    <span className="font-semibold">{stats.users.email_auth}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">New Today</span>
                    <span className="font-semibold text-blue-600">{stats.users.new_today}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-slate-600">New This Week</span>
                    <span className="font-semibold text-blue-600">{stats.users.new_this_week}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-slate-600">New This Month</span>
                    <span className="font-semibold text-blue-600">{stats.users.new_this_month}</span>
                  </div>
                </div>
              </div>

              {/* Signup Trends Chart */}
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp size={20} className="text-green-600" />
                  Signup Trends (Last 14 Days)
                </h3>
                <SimpleBarChart data={signupData} />
              </div>
            </div>

            {/* Organization Plans */}
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 size={20} className="text-purple-600" />
                Organizations by Plan
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-slate-700">{stats.organizations.free}</div>
                  <div className="text-sm text-slate-500 mt-1">Free Plan</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-blue-700">{stats.organizations.pro}</div>
                  <div className="text-sm text-blue-600 mt-1 flex items-center justify-center gap-1">
                    <Zap size={14} /> Pro Plan
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-purple-700">{stats.organizations.enterprise}</div>
                  <div className="text-sm text-purple-600 mt-1 flex items-center justify-center gap-1">
                    <Crown size={14} /> Enterprise
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Analytics */}
            {usageData && (
              <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Activity size={20} className="text-pink-600" />
                  Usage Analytics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-700">{usageData.active_organizations_last_30_days}</div>
                    <div className="text-xs text-green-600 mt-1">Active Orgs (30d)</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-700">{usageData.avg_rfqs_per_organization}</div>
                    <div className="text-xs text-blue-600 mt-1">Avg RFQs/Org</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-amber-700">
                      {usageData.rfqs_by_month.reduce((sum, m) => sum + m.count, 0)}
                    </div>
                    <div className="text-xs text-amber-600 mt-1">RFQs (6 months)</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-indigo-700">
                      {usageData.prs_by_month.reduce((sum, m) => sum + m.count, 0)}
                    </div>
                    <div className="text-xs text-indigo-600 mt-1">PRs (6 months)</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
                  className="pl-10"
                />
              </div>
              <span className="text-sm text-slate-500">{userTotal} total users</span>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">User</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Organization</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Plan</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Auth</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Joined</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map((user) => (
                    <tr key={user.id} className={`hover:bg-slate-50 ${user.is_active === false ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                              <Users size={14} className="text-slate-500" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-slate-900">{user.name}</div>
                            <div className="text-xs text-slate-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{user.organization_name}</td>
                      <td className="px-4 py-3">{getPlanBadge(user.organization_plan)}</td>
                      <td className="px-4 py-3">
                        {user.is_active === false ? (
                          <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Inactive</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${user.auth_provider === 'google' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                          {user.auth_provider || 'email'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {user.is_active === false ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestoreUser(user.id)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 px-2"
                              title="Restore user"
                            >
                              <RotateCcw size={14} />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteModal('user', user, false)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 px-2"
                              title="Deactivate user"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteModal('user', user, true)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            title="Permanently delete"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUserPage(p => Math.max(1, p - 1))}
                disabled={userPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </Button>
              <span className="text-sm text-slate-500">Page {userPage} of {Math.ceil(userTotal / 20)}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUserPage(p => p + 1)}
                disabled={userPage >= Math.ceil(userTotal / 20)}
              >
                Next <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Organizations Tab */}
        {activeTab === 'organizations' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Search organizations..."
                  value={orgSearch}
                  onChange={(e) => { setOrgSearch(e.target.value); setOrgPage(1); }}
                  className="pl-10"
                />
              </div>
              <span className="text-sm text-slate-500">{orgTotal} total organizations</span>
            </div>

            {/* Organizations Table */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Organization</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Owner</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Plan</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Users</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">RFQs</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Created</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {organizations.map((org) => (
                    <tr key={org.id} className={`hover:bg-slate-50 ${org.is_active === false ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{org.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-900">{org.owner_name}</div>
                        <div className="text-xs text-slate-500">{org.owner_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {org.is_active === false ? (
                          <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Inactive</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={org.plan || 'free'}
                          onChange={(e) => handlePlanChange(org.id, e.target.value)}
                          className="text-xs border rounded px-2 py-1 bg-white"
                        >
                          <option value="free">Free</option>
                          <option value="pro">Pro</option>
                          <option value="enterprise">Enterprise</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{org.user_count}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{org.rfq_count}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {org.is_active === false ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestoreOrg(org.id)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 px-2"
                              title="Restore organization"
                            >
                              <RotateCcw size={14} />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteModal('organization', org, false)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 px-2"
                              title="Deactivate organization"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteModal('organization', org, true)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            title="Permanently delete"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOrgPage(p => Math.max(1, p - 1))}
                disabled={orgPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </Button>
              <span className="text-sm text-slate-500">Page {orgPage} of {Math.ceil(orgTotal / 20)}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOrgPage(p => p + 1)}
                disabled={orgPage >= Math.ceil(orgTotal / 20)}
              >
                Next <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-full ${deleteModal.permanent ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <AlertTriangle size={24} className={deleteModal.permanent ? 'text-red-600' : 'text-amber-600'} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {deleteModal.permanent ? 'Permanently Delete' : 'Deactivate'} {deleteModal.type === 'user' ? 'User' : 'Organization'}
                </h3>
              </div>

              {deleteModal.type === 'user' && (
                <div className="mb-4">
                  <p className="text-slate-600">
                    {deleteModal.permanent 
                      ? `This will permanently delete the user "${deleteModal.item?.name}" (${deleteModal.item?.email}). This action cannot be undone.`
                      : `This will deactivate the user "${deleteModal.item?.name}" (${deleteModal.item?.email}). They will no longer be able to access the platform. You can restore them later.`
                    }
                  </p>
                </div>
              )}

              {deleteModal.type === 'organization' && (
                <div className="mb-4">
                  {deleteModal.permanent ? (
                    <>
                      <p className="text-slate-600 mb-3">
                        This will permanently delete the organization <strong>"{deleteModal.item?.name}"</strong> and ALL its data. This cannot be undone.
                      </p>
                      {deletePreview && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                          <p className="text-sm font-medium text-red-800 mb-2">Data that will be deleted:</p>
                          <ul className="text-sm text-red-700 space-y-1">
                            <li>• {deletePreview.will_delete.users} users</li>
                            <li>• {deletePreview.will_delete.rfqs} RFQs</li>
                            <li>• {deletePreview.will_delete.purchase_requisitions} Purchase Requisitions</li>
                            <li>• {deletePreview.will_delete.suppliers} Suppliers</li>
                            <li>• {deletePreview.will_delete.quotes} Quotes</li>
                          </ul>
                        </div>
                      )}
                      <div className="mb-3">
                        <Label className="text-sm text-slate-700">
                          Type <strong>"{deleteModal.item?.name}"</strong> to confirm:
                        </Label>
                        <Input
                          value={confirmName}
                          onChange={(e) => setConfirmName(e.target.value)}
                          placeholder="Type organization name"
                          className="mt-1"
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-600">
                      This will deactivate the organization <strong>"{deleteModal.item?.name}"</strong> and all {deleteModal.item?.user_count || 0} users. 
                      They will no longer be able to access the platform. You can restore them later.
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={closeDeleteModal} disabled={deleting}>
                  Cancel
                </Button>
                {deleteModal.type === 'user' && (
                  deleteModal.permanent ? (
                    <Button 
                      variant="destructive" 
                      onClick={() => handlePermanentDeleteUser(deleteModal.item?.id)}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                  ) : (
                    <Button 
                      className="bg-amber-600 hover:bg-amber-700"
                      onClick={() => handleSoftDeleteUser(deleteModal.item?.id)}
                      disabled={deleting}
                    >
                      {deleting ? 'Deactivating...' : 'Deactivate User'}
                    </Button>
                  )
                )}
                {deleteModal.type === 'organization' && (
                  deleteModal.permanent ? (
                    <Button 
                      variant="destructive" 
                      onClick={() => handlePermanentDeleteOrg(deleteModal.item?.id, deleteModal.item?.name)}
                      disabled={deleting || confirmName.trim().toLowerCase() !== (deleteModal.item?.name || '').trim().toLowerCase()}
                    >
                      {deleting ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                  ) : (
                    <Button 
                      className="bg-amber-600 hover:bg-amber-700"
                      onClick={() => handleSoftDeleteOrg(deleteModal.item?.id)}
                      disabled={deleting}
                    >
                      {deleting ? 'Deactivating...' : 'Deactivate Organization'}
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    pink: 'bg-pink-50 text-pink-600'
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        {React.cloneElement(icon, { size: 20 })}
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
};

export default AdminDashboard;
